import { describeApiError } from "../utils/apiError";
import { AnsweringClient } from "../clients/answeringClient";
import { environment } from '../utils/environment';

export interface IGroup {
  id: string;
  name: string;
  description?: string;
  instrument_title?: string;
  archived: string;
}

export interface IGroupToken {
  token: string;
}

export interface ManagedGroupsControllerDelegate {
  didGetGroups(groups: IGroup[]): void;
  filter({ page, total, filter }: { page: number; total: number, filter: string }): void;
  didGetFeedbackToken(token: string): void;
  shouldLogout(): void;
  genericError(error?: string): void;
}

export class ManagedGroupsController {
  constructor (
    private delegate: ManagedGroupsControllerDelegate,
    private readonly answeringClient = new AnsweringClient()
  ) {}

  public async getMyGroups(accessToken: string, options?: { page: number; limit: number }, alreadyRetried = false) {
    try {
      const response = await this.answeringClient.getMyGroups(accessToken, options),
      data = response.data.data as IGroup[];
      this.delegate.didGetGroups(data);
      this.delegate.filter({
        page: response.data.page,
        total: response.data.total,
        filter: response.data.filter || ""
      });
    } catch (error: any) {
      if(error?.response?.status === 401 && !alreadyRetried) {
        const newTokens = await this.refreshTokens();
        if(!newTokens) {
          return;
        }
        this.getMyGroups(newTokens.accessToken, options, true);
        return;
      }
      this.delegate.genericError(
        describeApiError(error, "Não foi possível carregar as devolutivas agregadas.")
      );
    }
  }

  public async getGroupFeedbackToken(accessToken: string, groupId: number, alreadyRetried = false) {
    try {
      const response = await this.answeringClient.getGroupFeedbackToken(accessToken, groupId);
      const token = response.data.token;
      this.delegate.didGetFeedbackToken(token);
    } catch (error: any) {
      if(error?.response?.status === 401 && !alreadyRetried) {
        const newTokens = await this.refreshTokens();
        if(!newTokens) {
          return;
        }
        this.getGroupFeedbackToken(newTokens.accessToken, groupId, true);
        return;
      }
      this.delegate.genericError(
        describeApiError(error, "Não foi possível abrir a devolutiva agregada.")
      );
    }
  }

  public async getGroupInviteToken(accessToken: string, groupId: number, alreadyRetried = false) {
    try {
      const response = await this.answeringClient.getGroupInviteToken(accessToken, groupId),
      token = response.data.token;
      navigator.clipboard.writeText(`${environment.instrumentsFrontendBaseUrl}/invite?invite_token=${token}`);
    } catch (error: any) {
      if(error?.response?.status === 401 && !alreadyRetried) {
        const newTokens = await this.refreshTokens();
        if(!newTokens) {
          return;
        }
        this.getGroupInviteToken(newTokens.accessToken, groupId, true);
        return;
      }
      this.delegate.genericError("Erro ao copiar link do grupo");
    }
  }

  private async refreshTokens(): Promise<{accessToken: string, refreshToken: string} | void> {
    const refreshToken = localStorage.getItem("RefreshToken");
    if(!refreshToken) {
      this.delegate.shouldLogout();
      return;
    }
    try {
      const response = await this.answeringClient.refreshToken(refreshToken);
      const tokens: {accessToken: string, refreshToken: string} = response.data;
      localStorage.setItem("AccessToken", tokens.accessToken);
      localStorage.setItem("RefreshToken", tokens.refreshToken);
      return tokens;
    } catch (error: any) {
      this.delegate.shouldLogout();
      return;
    }
  }
}
